import React from 'react'
import { useHistory } from 'react-router'
import classNames from 'classnames'
import propTypes from 'prop-types'
import { FaUserCircle } from 'react-icons/fa'
import { MdAlbum, MdOutlineEvent, MdOutlinePlayCircleOutline } from "react-icons/md"
import { Transition } from '@headlessui/react'
import { createPortal } from 'react-dom'

import Menu from 'components/Menu'
import Loader from 'components/Loader'
import { AvatarImage } from 'components/Avatar'
import useAuthStore from 'store/auth'
import useGlobalStore from 'store'


export function Container(props) {
  return (
    <div className={classNames([
      'flex flex-grow',
      props.className,
    ])}>
      {props.children}
    </div>
  )
}

Container.propTypes = {
  className: propTypes.string,
}


export function Body(props) {
  return (
    <div className={classNames([
      'flex-grow',
      { 'p-5 sm:p-20': ! props.noPadding },
      props.className,
    ])}>
      {
        props.loading &&
        <div className='
          w-full h-full
          fixed left-0 right-0 top-0 bottom-0
          flex items-center justify-center
          bg-white bg-opacity-50
          z-50
        '>
          <Loader active className='text-primary bg-white p-3 h-20 w-20 rounded-full' />
        </div>
      }
      {props.children}
    </div>
  )
}

Body.propTypes = {
  className: propTypes.string,
  noPadding: propTypes.bool,
  loading: propTypes.bool,
}
Body.defaultProps = {
  loading: false,
}

export function ActionButtonsGroup(props) {
  const { showActionButton } = useGlobalStore()

  const countElements = React.Children.count(props.children)

  const [show, setShow] = React.useState(false)
  React.useEffect(() => {

    setShow(true)

    if (countElements === 1) {
      showActionButton(true)
    }

    return () => {
      showActionButton(false)
      setShow(false)
    }
  }, [countElements]) // eslint-disable-line

  const portal = createPortal(
    <Transition
      as={React.Fragment}
      show={show}
      enter='transition-all duration-700 ease-in-out'
      enterFrom='opacity-0 scale-50'
      enterTo='opacity-100 scale-100'
      leave='transition-all duration-700 ease-in-out'
      leaveFrom='opacity-100 scale-100'
      leaveTo='opacity-0 scale-50'
    >
      <div className={classNames([
        'fixed flex z-30 transform lg:transform-none lg:right-10 lg:left-auto',
        'rounded-full bg-white p-2',
        'space-x-2',
        {
          'bottom-8 -shadow-lg -translate-x-1/2 left-1/2': countElements === 1,
          'bottom-20 shadow-lg left-auto right-3': countElements > 1,
        },
        props.className,
      ])}>
        {props.children}
      </div>
    </Transition>,
    document.querySelector('body'),
  )

  return portal
}

ActionButtonsGroup.propTypes = {
  children: propTypes.oneOfType([
    propTypes.shape({
      type: propTypes.oneOf([ActionButton]),
    }),
    propTypes.arrayOf(
      propTypes.shape({
        type: propTypes.oneOf([ActionButton]),
      })
    ),
  ]).isRequired,
  className: propTypes.string,
}



export function ActionButton(props) {
  return (
    <div
      className={classNames([
        'action-button',
        props.theme,
        props.className,
        {
          'cursor-wait': props.loading,
          'cursor-default': props.disabled,
          'cursor-pointer': !props.loading && !props.disabled,
        },
      ])}
      onClick={!props.loading ? props.onClick : () => {}}
    >
      {
        props.loading ?
        <Loader className='text-white' active /> :
        props.children
      }
    </div>
  )
}

ActionButton.propTypes = {
  active: propTypes.bool,
  className: propTypes.string,
  theme: propTypes.oneOf(['default', 'warning', 'danger', 'secondary', 'disabled']),
  onClick: propTypes.func,
  loading: propTypes.bool,
  disabled: propTypes.bool,
}

ActionButton.defaultProps = {
  theme: 'default',
}


export function Header(props) {
  return (
    <div className={classNames([
      'flex z-10',
      props.className,
    ])}>
      {props.children}
    </div>
  )
}

Header.propTypes = {
  className: propTypes.string,
}


export function Basic(props) {
  const history = useHistory()
  const {isLogged, data: user} = useAuthStore()
  const { actionButtonShowed } = useGlobalStore()

  const [active, setActive] = React.useState(null)

  const path = history.location.pathname.split('/')[1]
  React.useEffect(() => {
    setActive(path)
  }, [path])

  const onClick = value => {
    setActive(value)
    setTimeout(() => history.push(value), 100)
  }

  return (
    <div className='flex flex-col w-full bg-body transition-all'>
      { props.children }
      {
        <Menu position='bottom' containerClassName='z-20 sticky bottom-0 left-0 w-full'>
          <Menu.Item
            linkClassName='py-2'
            title='compositions'
            active={active === ''}
            onClick={() => onClick('/')}
            icon={MdOutlinePlayCircleOutline}
          />
          <Menu.Item
            linkClassName='py-2'
            title='albums'
            active={active === 'albums'}
            onClick={() => onClick('/albums')}
            icon={MdAlbum}
          />
          {
            actionButtonShowed &&
            <div className='flex-grow flex-1 lg:hidden'/>
          }
          <Menu.Item
            linkClassName='py-2'
            title='events'
            active={active === 'events'}
            onClick={() => onClick('/events')}
            icon={MdOutlineEvent}
          />
          {
            isLogged ?
            <Menu.Item
              linkClassName='py-2'
              title='account'
              active={active === 'account'}
              onClick={() => onClick('/account')}
            >
              <AvatarImage
                user={user}
                size={true}
                active={active === 'account'}
                hover
                className='w-12 h-12'
              />
            </Menu.Item> :
            <Menu.Item
              linkClassName='py-2'
              title='login'
              active={active === 'login'}
              onClick={() => onClick('/login')}
              icon={FaUserCircle}
            />
          }
        </Menu>
      }
    </div>
  )
}

Basic.defaultProps = {
  children: propTypes.oneOfType([
    propTypes.shape({
      type: propTypes.oneOf([Header, Body]),
    }),
    propTypes.arrayOf(
      propTypes.shape({
        type: propTypes.oneOf([Header, Body]),
      })
    ),
  ]),
}

Basic.Body = Body
Basic.Header = Header
